import React, { useState, useEffect, useContext } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClipboardList, faSave } from '@fortawesome/free-solid-svg-icons'
import Swal from 'sweetalert2'
import { useParams } from 'react-router-dom'
import CustomLoader from '../layouts/CustomLoader'
import DatePicker from 'react-datepicker'
import { DateTime } from 'luxon'
import AuthContext from '../../middlewares/AuthContext'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface StatusOption {
  id   : number
  name : string
}

interface LicensePackage {
  serial_number: string
}

interface License {
  StatusId  : number
  expireAt  : string
  product_key: string
  Package   : LicensePackage
}

interface ApiResponse {
  message  : License
  statuses : StatusOption[]
  response?: string
}

const statusColor: Record<number, string> = {
  1: '#6eb446',
  2: '#ef4444',
  3: '#3b82f6',
  4: '#3b82f6',
}

const formSchema = Yup.object().shape({
  StatusId: Yup.number().min(1).max(7).required(),
})

const EditLicense: React.FC = () => {
  const { getHeaders }            = useContext(AuthContext) as AuthCtx
  const { t }                     = useTranslation()
  const { id }                    = useParams<{ id: string }>()

  const [license, setLicense]     = useState<License | null>(null)
  const [statuses, setStatuses]   = useState<StatusOption[]>([])
  const [loaded, setLoaded]       = useState(false)
  const [loading, setLoading]     = useState(false)
  const [expireDate, setExpireDate] = useState<Date | null>(null)
  const [dateError, setDateError] = useState(false)

  useEffect(() => {
    const fetch = async () => {
      try {
        const headers = await getHeaders()
        const res     = await axios.get<ApiResponse>(`${url}licenses/edit/${id}`, { headers })
        setLicense(res.data.message)
        setStatuses(res.data.statuses)
        setExpireDate(new Date(res.data.message.expireAt))
      } catch (err: any) {
        Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
      } finally {
        setLoaded(true)
      }
    }
    fetch()
  }, [])

  const handleSubmit = async (values: { StatusId: number }) => {
    if (!expireDate) {
      setDateError(true)
      return
    }
    const dt = DateTime.fromJSDate(expireDate)
    if (!dt.isValid) {
      setDateError(true)
      return
    }
    setDateError(false)
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.put<{ response: string; message: License }>(
        `${url}licenses/update/${id}`,
        { ...values, expireAt: dt.toFormat('yyyy-MM-dd') },
        { headers }
      )
      setLicense(res.data.message)
      Swal.fire({ icon: 'success', title: res.data.response })
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  if (!loaded) return <CustomLoader />

  if (!license) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <p className="text-muted">License not found.</p>
      </div>
    )
  }

  const currentStatus = statuses.find(s => s.id === license.StatusId)
  const badgeColor    = statusColor[license.StatusId] ?? '#94a3b8'

  return (
    <div className="row justify-content-center">
      <div className="col-md-8 col-lg-6">

        {/* ── Header card ──────────────────────────────────────────── */}
        <div style={{
          backgroundColor : '#fff',
          borderRadius    : 16,
          boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
          border          : '1px solid #f1f5f9',
          overflow        : 'hidden',
          marginBottom    : '1.5rem',
        }}>
          <div style={{ height: 4, background: `linear-gradient(90deg, ${BRAND_DARK}, ${BRAND})` }} />
          <div style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width           : 40,
              height          : 40,
              borderRadius    : 10,
              backgroundColor : `${BRAND}18`,
              display         : 'flex',
              alignItems      : 'center',
              justifyContent  : 'center',
              flexShrink      : 0,
            }}>
              <FontAwesomeIcon icon={faClipboardList} style={{ color: BRAND, fontSize: '1.1rem' }} />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {t('packages.show.edit')}
              </p>
              <p style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
                {license.product_key}
              </p>
            </div>
          </div>
        </div>

        {/* ── Info strip ───────────────────────────────────────────── */}
        <div style={{
          backgroundColor : '#f8fafc',
          borderRadius    : 12,
          border          : '1px solid #e2e8f0',
          padding         : '1rem 1.25rem',
          marginBottom    : '1.25rem',
          display         : 'flex',
          flexWrap        : 'wrap',
          gap             : '1rem',
        }}>
          <div>
            <p style={{ margin: 0, fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
              {t('licenses.product_key')}
            </p>
            <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 700, color: '#1e293b', fontFamily: 'monospace' }}>
              {license.product_key}
            </p>
          </div>
          <div>
            <p style={{ margin: 0, fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
              {t('licenses.package')}
            </p>
            <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 700, color: '#1e293b' }}>
              {license.Package.serial_number}
            </p>
          </div>
          <div>
            <p style={{ margin: 0, fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
              {t('licenses.customer.status')}
            </p>
            <span style={{
              display         : 'inline-block',
              padding         : '2px 10px',
              borderRadius    : 9999,
              backgroundColor : `${badgeColor}18`,
              color           : badgeColor,
              fontWeight      : 700,
              fontSize        : '0.78rem',
              textTransform   : 'capitalize',
              border          : `1px solid ${badgeColor}40`,
            }}>
              {currentStatus?.name ?? '—'}
            </span>
          </div>
        </div>

        {/* ── Form card ────────────────────────────────────────────── */}
        <div style={{
          backgroundColor : '#fff',
          borderRadius    : 16,
          boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
          border          : '1px solid #f1f5f9',
          overflow        : 'hidden',
        }}>
          <div style={{ height: 3, backgroundColor: BRAND }} />
          <div style={{ padding: '1.5rem' }}>
            <Formik
              initialValues={{ StatusId: license.StatusId }}
              validationSchema={formSchema}
              onSubmit={handleSubmit}
            >
              <Form>
                {/* Expire at */}
                <div className="mb-4">
                  <label
                    htmlFor="expireAt"
                    style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                  >
                    {t('licenses.customer.expire_at')}
                  </label>
                  <DatePicker
                    id="expireAt"
                    dateFormat="yyyy-MM-dd"
                    selected={expireDate}
                    onChange={(date) => { setExpireDate(date); setDateError(false) }}
                    className="form-control form-control-sm"
                    placeholderText="yyyy-MM-dd"
                  />
                  {dateError && (
                    <p style={{ margin: '0.35rem 0 0', fontSize: '0.78rem', color: '#ef4444', fontWeight: 600 }}>
                      Invalid date
                    </p>
                  )}
                </div>

                {/* Status */}
                <div className="mb-4">
                  <label
                    htmlFor="StatusId"
                    style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                  >
                    {t('packages.status_license')}
                  </label>
                  <Field
                    as="select"
                    id="StatusId"
                    name="StatusId"
                    className="form-select form-select-sm text-capitalize"
                  >
                    {statuses.map((s) => (
                      <option key={s.id} value={s.id} className="text-capitalize">
                        {s.name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage name="StatusId" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    display         : 'inline-flex',
                    alignItems      : 'center',
                    gap             : '0.5rem',
                    padding         : '0.55rem 1.5rem',
                    borderRadius    : 9999,
                    backgroundColor : loading ? '#94a3b8' : BRAND,
                    color           : '#fff',
                    fontWeight      : 700,
                    fontSize        : '0.875rem',
                    border          : 'none',
                    cursor          : loading ? 'not-allowed' : 'pointer',
                    boxShadow       : loading ? 'none' : `0 4px 14px ${BRAND}55`,
                    transition      : 'filter 0.15s, transform 0.15s',
                    width           : '100%',
                    justifyContent  : 'center',
                  }}
                  onMouseEnter={e => { if (!loading) { (e.currentTarget as HTMLButtonElement).style.filter = 'brightness(1.09)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)' }}}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.filter = 'brightness(1)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)' }}
                >
                  {loading
                    ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    : <FontAwesomeIcon icon={faSave} />
                  }
                  <span>{t('packages.show.edit')}</span>
                </button>
              </Form>
            </Formik>
          </div>
        </div>

      </div>
    </div>
  )
}

export default EditLicense
